import React, { useState } from 'react';
import { Stack, Typography, Box, Button, Alert } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import { toast } from 'react-toastify';

import { DenseTable, DenseColumn } from '../../../../components/groups-teams/shared/DenseTable';
import { GT_COLORS } from '../../../../constants/designTokens.constants';
import { bulkActionsApi } from '../../../../services/bulk-actions/bulkActionsApi';
import { BulkDeleteCommitResponse, CommitFailure } from '../../../../types/bulkActions.types';

interface ResultStepProps {
    commitResult: BulkDeleteCommitResponse;
    attemptId: string;
    onStartNew: () => void;
}

const statusConfig: Record<string, { severity: 'success' | 'warning' | 'error'; getMessage: (r: BulkDeleteCommitResponse) => string }> = {
    COMPLETED: {
        severity: 'success',
        getMessage: (r) => `All ${r.totalSuccessful} user${r.totalSuccessful !== 1 ? 's were' : ' was'} successfully deleted.`,
    },
    PARTIAL: {
        severity: 'warning',
        getMessage: (r) => `${r.totalSuccessful} of ${r.totalAttempted} user${r.totalAttempted !== 1 ? 's' : ''} deleted. ${r.totalFailed} failed.`,
    },
    FAILED: {
        severity: 'error',
        getMessage: (r) => r.totalSuccessful > 0
            ? `Only ${r.totalSuccessful} of ${r.totalAttempted} users deleted. ${r.totalFailed} failed.`
            : `Bulk delete failed. No users were deleted.`,
    },
};

const failureColumns: DenseColumn<CommitFailure>[] = [
    {
        id: 'rowNumber',
        label: 'Row',
        width: 70,
        sortable: true,
        render: (row) => <Typography fontSize={13}>{row.rowNumber}</Typography>,
        getSortValue: (row) => row.rowNumber,
    },
    {
        id: 'userId',
        label: 'User ID',
        width: 240,
        render: (row) => <Typography fontSize={12} fontFamily="monospace" color={GT_COLORS.textMuted}>{row.userId}</Typography>,
    },
    {
        id: 'error',
        label: 'Error',
        render: (row) => <Typography fontSize={13} color={GT_COLORS.red}>{row.error}</Typography>,
    },
];

export const ResultStep: React.FC<ResultStepProps> = ({
    commitResult,
    attemptId,
    onStartNew,
}) => {
    const [isDownloading, setIsDownloading] = useState(false);

    const config = statusConfig[commitResult.status] || statusConfig.FAILED;

    const handleDownloadReport = async () => {
        setIsDownloading(true);
        try {
            await bulkActionsApi.downloadReport(attemptId);
        } catch (err) {
            toast.error('Failed to download report. Please try again.');
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Stack spacing={3}>
            {/* Status banner */}
            <Alert severity={config.severity} sx={{ borderRadius: '8px', fontSize: 15 }}>
                {config.getMessage(commitResult)}
            </Alert>

            {/* Summary stats */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                    { label: 'Total Attempted', value: commitResult.totalAttempted, bg: GT_COLORS.headerBg, color: GT_COLORS.textPrimary },
                    { label: 'Successful', value: commitResult.totalSuccessful, bg: GT_COLORS.greenBg, color: GT_COLORS.green },
                    { label: 'Failed', value: commitResult.totalFailed, bg: commitResult.totalFailed > 0 ? GT_COLORS.redBg : GT_COLORS.headerBg, color: commitResult.totalFailed > 0 ? GT_COLORS.red : GT_COLORS.textMuted },
                ].map(stat => (
                    <Box
                        key={stat.label}
                        sx={{
                            flex: '1 1 140px',
                            p: 2,
                            borderRadius: '8px',
                            backgroundColor: stat.bg,
                            textAlign: 'center',
                        }}
                    >
                        <Typography fontSize={24} fontWeight={700} color={stat.color}>
                            {stat.value}
                        </Typography>
                        <Typography fontSize={12} color={GT_COLORS.textSecondary}>
                            {stat.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Failures table */}
            {commitResult.failures.length > 0 && (
                <Box sx={{ border: `1px solid ${GT_COLORS.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${GT_COLORS.borderLight}` }}>
                        <Typography fontSize={14} fontWeight={600} color={GT_COLORS.textPrimary}>
                            Failures ({commitResult.failures.length})
                        </Typography>
                    </Box>
                    <DenseTable<CommitFailure>
                        columns={failureColumns}
                        rows={commitResult.failures}
                        getRowKey={(row) => `failure-${row.rowNumber}`}
                        pageSize={100}
                        defaultSortColumn="rowNumber"
                    />
                </Box>
            )}

            {/* Action buttons */}
            <Stack direction="row" justifyContent="space-between">
                <Button
                    variant="outlined"
                    startIcon={<ReplayIcon />}
                    onClick={onStartNew}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Start New Bulk Action
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={isDownloading}
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadReport}
                    sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                    Download Report
                </LoadingButton>
            </Stack>
        </Stack>
    );
};
